import { useState, useEffect } from "react";
import { useGqlMutation, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { RecordScreenCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenCard";
import { updateCityMutation } from "@/app/Cities/Cities/GraphQL/cityMutations";
import { generateCityDescriptionMutation } from "@/app/Cities/Cities/GraphQL/cityDescriptionMutation";
import { cityRecordQuery } from "@/app/Cities/Cities/GraphQL/cityRecordQuery";
import { toast } from "sonner";
import { Textarea } from "@/components/catalyst/textarea";
import { Button } from "@/components/catalyst/button";
import { EmptyState } from "@/lib/Components/EmptyState/EmptyState";
import { BotMessageSquareIcon, FileTextIcon } from "lucide-react";

type CityDescriptionCardProps = {
  cityId: string;
  description: string | null | undefined;
};

export function CityDescriptionCard({
  cityId,
  description: initialDescription,
}: CityDescriptionCardProps) {
  const { refetch } = useGqlQuery(cityRecordQuery, { id: cityId });
  const { mutateAsync: generateDescription, isPending: isGenerating } =
    useGqlMutation(generateCityDescriptionMutation);
  const { mutateAsync: updateCity, isPending: isSaving } =
    useGqlMutation(updateCityMutation);

  const [description, setDescription] = useState(initialDescription || "");
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setDescription(initialDescription || "");
  }, [initialDescription]);

  const handleGenerate = async () => {
    try {
      const result = await generateDescription({ id: cityId });
      setDescription(result.generateCityDescription.description || "");
      toast.success("Description generated!");
    } catch (error) {
      toast.error("Failed to generate description");
      console.error(error);
    }
  };

  const handleSave = async () => {
    try {
      await updateCity({
        id: cityId,
        input: { description },
      });
      setIsEditing(false);
      toast.success("Description saved!");
      refetch();
    } catch (error) {
      toast.error("Failed to save description");
      console.error(error);
    }
  };

  return (
    <RecordScreenCard
      title="Description"
      subtitle="AI-generated description for deals page"
      buttons={
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button
                onClick={() => {
                  setDescription(initialDescription || "");
                  setIsEditing(false);
                }}
                outline
              >
                Cancel
              </Button>
              <Button onClick={handleSave} color="green" disabled={isSaving}>
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </>
          ) : (
            <>
              <Button
                LeadingIcon={BotMessageSquareIcon}
                onClick={handleGenerate}
                color="blue"
                disabled={isGenerating}
              >
                {isGenerating
                  ? "Generating..."
                  : description
                    ? "Regenerate"
                    : "Generate"}
              </Button>
              {description ? (
                <Button onClick={() => setIsEditing(true)} outline>
                  Edit
                </Button>
              ) : null}
            </>
          )}
        </div>
      }
    >
      {isEditing ? (
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={6}
          className="w-full"
        />
      ) : description ? (
        <p className="whitespace-pre-wrap text-sm text-gray-700">
          {description}
        </p>
      ) : (
        <EmptyState
          Icon={FileTextIcon}
          header="No Description"
          subheader="Generate an AI description for this city"
        />
      )}
    </RecordScreenCard>
  );
}
